import type { LmsAssignmentInput } from '../services/api';

/**
 * Reconciling the audience picked on an editor with the rows already in
 * lms_module_assignments.
 *
 * The assignment endpoints are additive (`ON CONFLICT DO NOTHING`) and deletion
 * is per-row by id, so "save what is on screen" is a diff, not a replace. Doing
 * it here rather than in each editor keeps sessions and journeys from drifting
 * apart — they write to the same table through differently-named endpoints.
 */

export interface ExistingLmsAssignment {
  id: string;
  employee_code?: string | null;
  role_id?: string | null;
}

export interface LmsAssignmentDiff {
  toCreate: LmsAssignmentInput[];
  toDeleteIds: string[];
}

/**
 * Rows to add and rows to remove so the stored assignments match `roleIds` +
 * `employeeCodes`. Rows that are neither (an assignment that should stay) are
 * left untouched, so re-saving an unchanged editor issues no writes at all.
 */
export function diffLmsAssignments(
  existing: ExistingLmsAssignment[],
  roleIds: string[],
  employeeCodes: string[]
): LmsAssignmentDiff {
  const wantedRoles = new Set(roleIds);
  const wantedEmployees = new Set(employeeCodes);

  const haveRoles = new Set<string>();
  const haveEmployees = new Set<string>();
  const toDeleteIds: string[] = [];

  existing.forEach((row) => {
    const roleId = row.role_id ?? null;
    const employeeCode = row.employee_code ?? null;

    if (roleId) {
      // A duplicate row for a role we already kept is dead weight; drop it so
      // repeated saves cannot accumulate copies.
      if (wantedRoles.has(roleId) && !haveRoles.has(roleId)) {
        haveRoles.add(roleId);
      } else {
        toDeleteIds.push(row.id);
      }
      return;
    }

    if (employeeCode) {
      if (wantedEmployees.has(employeeCode) && !haveEmployees.has(employeeCode)) {
        haveEmployees.add(employeeCode);
      } else {
        toDeleteIds.push(row.id);
      }
    }
  });

  const toCreate: LmsAssignmentInput[] = [
    ...roleIds
      .filter((roleId) => !haveRoles.has(roleId))
      .map((roleId) => ({ type: 'role' as const, role_id: roleId })),
    ...employeeCodes
      .filter((code) => !haveEmployees.has(code))
      .map((code) => ({ type: 'employee' as const, employee_code: code })),
  ];

  return { toCreate, toDeleteIds };
}

/**
 * Apply a diff. `createAssignments` is only called when there is something to
 * create — the endpoint rejects an empty array with a 400, which would surface
 * as a spurious "failed to save" on an edit that only removed people.
 */
export async function applyLmsAssignmentDiff(
  diff: LmsAssignmentDiff,
  handlers: {
    createAssignments: (inputs: LmsAssignmentInput[]) => Promise<unknown>;
    deleteAssignment: (assignmentId: string) => Promise<unknown>;
  }
): Promise<void> {
  if (diff.toCreate.length > 0) {
    await handlers.createAssignments(diff.toCreate);
  }
  for (const assignmentId of diff.toDeleteIds) {
    await handlers.deleteAssignment(assignmentId);
  }
}

/** Split stored assignment rows back into the two lists the picker binds to. */
export function splitLmsAssignments(existing: ExistingLmsAssignment[]): {
  roleIds: string[];
  employeeCodes: string[];
} {
  const roleIds: string[] = [];
  const employeeCodes: string[] = [];
  existing.forEach((row) => {
    if (row.role_id) {
      if (!roleIds.includes(row.role_id)) roleIds.push(row.role_id);
    } else if (row.employee_code) {
      if (!employeeCodes.includes(row.employee_code)) employeeCodes.push(row.employee_code);
    }
  });
  return { roleIds, employeeCodes };
}
